<script setup>
import {ref, shallowRef, onMounted, useTemplateRef, computed} from 'vue';
import {RMModal} from 'redmine-ui-kit';

import {useQueryRepository} from '@/core/repository/query';
import {useQueryBus} from '@/core/plugins/queryBus';
import {getParamValue, updateUrlParams} from '@/core/helpers/urlParams';
import {QUERY_ACTIONS} from '@/core/constants/query';
import {useModalBus} from '@/core/composables/useModalBus';

const props = defineProps({
    modalData: {
        type: Object,
        required: true,
    },
    isActive: {
        type: Boolean,
        default: () => false,
    },
});

const {closeModal} = useModalBus();

const model = ref({
    query: {
        name: '',
        description: '',
    },
});
const queryData = shallowRef(null);
const isFetching = shallowRef(false);
const formRef = useTemplateRef('form');
const queryName = shallowRef('');

const modalTitle = computed(() => {
    switch (props.modalData.action) {
        case QUERY_ACTIONS.SAVE:
            return 'filters.label_save_filter';

        case QUERY_ACTIONS.EDIT:
            return 'filters.label_edit_filter';
    
        case QUERY_ACTIONS.RENAME:
            return 'filters.label_rename_filter';
    
        default:
            return 'filters.label_save_filter';
    }
});

async function handleSubmit(e) {
    if (e.key === 'Enter' && !formRef.value.checkValidity()) {
        return formRef.value.reportValidity();
    }

    if (queryData.value instanceof FormData === false) {
        console.error('Type of query data must be a FormData');

        return;
    }

    try {
        isFetching.value = true;
        const formData = new FormData(formRef.value);
        queryData.value.append('set_filter', 1);

        for (const [key, value] of formData) {
            queryData.value.append(key, value);
        }

        if (props.modalData.action === QUERY_ACTIONS.SAVE) {
            const response = await useQueryRepository().createQuery(queryData.value);

            if (response.id) {
                updateUrlParams({query_id: response.id});
            }
        }

        if ([QUERY_ACTIONS.EDIT, QUERY_ACTIONS.RENAME].includes(props.modalData.action)) {
            await useQueryRepository().updateQuery(getParamValue('query_id'), queryData.value);
        }

        if (typeof props.modalData.callback === 'function') {
            props.modalData.callback();
        }

        useQueryBus().listUpdate();
        closeModal(props.modalData.template);
    } catch (error) {
        console.log(error);
    } finally {
        isFetching.value = false;
    }
}

onMounted(() => {
    formRef.value?.querySelector('input')?.focus();

    queryData.value = props.modalData.queryData;

    if ([QUERY_ACTIONS.EDIT, QUERY_ACTIONS.RENAME].includes(props.modalData.action)) {
        useQueryRepository().getQuery(getParamValue('query_id')).then(({name}) => {
            model.value.query.name = name;
            queryName.value = name;
        });
    }
});
</script>

<template>
    <RMModal
        :is-active="isActive"
        transition-name="modal-fade"
        @close="closeModal(modalData.template)"
    >
        <form
            ref="form"
            class="chart-query-save"
            @keypress.enter.stop.prevent
            @keydown.enter.stop.prevent
            @keyup.enter.stop.prevent="handleSubmit"
            @submit.prevent="handleSubmit"
        >
            <h3>
                {{ $t(modalTitle, {name: queryName}) }}
            </h3>
            <div
                v-show="[QUERY_ACTIONS.SAVE, QUERY_ACTIONS.RENAME].includes(props.modalData.action)"
                class="chart-query-save__control"
            >
                <label
                    for="query-name"
                    class="fz-12"
                >
                    {{ $t('filters.label_report_name') }}
                </label>
                <input
                    id="query-name"
                    v-model="model.query.name"
                    name="query[name]"
                    :placeholder="$t('filters.label_query_new')"
                    type="text"
                    required
                >
            </div>
            <!-- <textarea
                id="query-description"
                v-model="model.query.description"
                name="query[description]"
                :placeholder="$t('label_description')"
            /> -->
            <div class="flex justify-end gap-16">
                <button
                    type="submit"
                    class="ml-auto"
                    @click.prevent="closeModal(modalData.template)"
                >
                    {{ $t('label_cancel') }}
                </button>
                <button
                    type="submit"
                    :disabled="isFetching"
                >
                    {{ $t('label_save') }}
                </button>
            </div>
        </form>
    </RMModal>
</template>

<style lang="scss">
.chart-query-save {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__control {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
}
</style>
